import { useEffect, useState } from "react";
import { toast } from "react-toastify";

/**
 * お知らせ一覧ページ用のカスタムフック
 *
 * - お知らせ一覧の取得
 * - ページネーション管理
 * - 初回ページ表示時の既読化処理
 */
export default function useNoticesIndex() {
  /** お知らせ一覧データ */
  const [notices, setNotices] = useState([]);

  /** ページネーション情報（total_pages など） */
  const [pagination, setPagination] = useState(null);

  /** ローディング状態 */
  const [loading, setLoading] = useState(true);

  /** 現在のページ番号 */
  const [page, setPage] = useState(1);

  /**
   * ページ変更時にお知らせ一覧を取得
   *
   * page が変わるたびに再取得する
   */
  useEffect(() => {
    const controller = new AbortController();

    const fetchNotices = async () => {
      try {
        // ローディング開始
        setLoading(true);

        // お知らせ一覧取得API
        const response = await fetch(`/api/notices?page=${page}`, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
          signal: controller.signal,
        });

        // ステータス異常時はエラー扱い
        if (!response.ok) throw new Error();

        const data = await response.json();

        // APIレスポンスをstateに反映
        setNotices(data.notices ?? []);
        setPagination(data.pagination ?? null);

        /**
         * 1ページ目を表示した場合のみ既読化
         *
         * - 一覧を開いたら「既読」とみなす仕様
         * - 2ページ目以降では既読処理しない
         */
        if (page === 1) {
          try {
            const markReadResponse = await fetch(
              "/api/notices/mark_notice_read",
              {
                method: "POST",
                headers: {
                  Accept: "application/json",
                  "Content-Type": "application/json",
                  "X-CSRF-Token": document
                    .querySelector('meta[name="csrf-token"]')
                    ?.getAttribute("content"),
                },
                credentials: "same-origin",
                signal: controller.signal,
              },
            );

            // 成功時のみ未読バッジを更新
            if (markReadResponse.ok) {
              window.dispatchEvent(
                new CustomEvent("app:setUnreadNotice", {
                  detail: { hasUnreadNotice: false },
                }),
              );
            } else {
              toast.error("既読更新に失敗しました。");
            }
          } catch (error) {
            // リクエスト中断時は何もしない
            if (error.name === "AbortError") return;

            // ネットワークエラーなど
            toast.error("既読更新に失敗しました。");
          }
        }
      } catch (error) {
        // リクエスト中断時は何もしない
        if (error.name === "AbortError") return;

        // 一覧取得失敗
        toast.error("情報の読み込みに失敗しました。再読み込みしてください。");
        setNotices([]);
        setPagination(null);
      } finally {
        // ローディング終了
        setLoading(false);
      }
    };

    fetchNotices();

    return () => {
      controller.abort();
    };
  }, [page]);

  /**
   * ページ変更処理
   *
   * UI側でスクロールなどを追加したいので
   * hookではシンプルにstate更新だけにする
   */
  const handlePageChange = (nextPage) => {
    setPage(nextPage);
  };

  return {
    notices,
    pagination,
    loading,
    page,
    setPage,
    handlePageChange,
  };
}
